/**
 * Legacy sound-settings shim (#231).
 *
 * The canonical store is `src/lib/alerts/alertPreferences.ts` — same
 * `galactus:soundSettings` key, additive v2 schema (`channels` +
 * `schemaVersion`) with every legacy flat field mirrored on write. This
 * module keeps the pre-#231 API surface (`SoundSettings`, `SOUND_DEFAULTS`,
 * `readSoundSettings`, `writeSoundSettings`) so existing consumers
 * (accountStore, tradeAudio, audioContext, PositionTracker, the Settings
 * panels) compile and behave unchanged.
 *
 * - `readSoundSettings` is the legacy VIEW of the canonical state.
 *   `queueFrontSoundEnabled` reads as OR of the queueWarning /
 *   queueCritical / fellBehind channels.
 * - `patchSoundSettings` is the WRITE PATH EVERY LEGACY CALLER SHOULD USE. It
 *   reads current canonical truth and applies only the keys the caller
 *   explicitly passed, so a stale snapshot cannot revert a field someone else
 *   moved, and it returns the PERSISTED legacy view so callers adopt
 *   normalized values instead of their own pre-write guess.
 *
 * - `writeSoundSettings` takes a WHOLE legacy object and applies a
 *   changed-field diff against the current canonical snapshot. It is retained
 *   for a deliberate whole-object replacement only. What the diff buys:
 *   per-channel data the legacy shape CANNOT REPRESENT (per-alert volumes,
 *   #233 fields, unknown channel ids) is never clobbered by a legacy write.
 *
 *   What it does NOT buy: freshness. A caller holding a stale snapshot still
 *   reverts any LEGACY-REPRESENTABLE field it read before someone else moved
 *   it — booleans and numerics alike, because the writer cannot tell "user
 *   deliberately set true" from "snapshot was true when I mounted". That is
 *   exactly the race `patchSoundSettings` exists to close: with explicit-key
 *   semantics there is no untouched field to write back.
 *
 * Known lossy edge, inherent to the 3->1 queue mapping: toggling the legacy
 * queue master OFF then ON turns all three queue channels on, discarding a
 * per-channel split. Both writers diff-guard the trio, so re-asserting an
 * unchanged master value is a no-op and only a deliberate user flip does this
 * — not a bug to chase in #232.
 */

import {
  readAlertPreferences,
  writeAlertPreferences,
  STORAGE_KEY as CANONICAL_STORAGE_KEY,
  type AlertPreferences,
} from '@/lib/alerts/alertPreferences';
import {
  ALERT_CHANNEL_IDS,
  ALERT_REGISTRY,
  type AlertChannelId,
  type LegacySoundToggleKey,
} from '@/lib/alerts/registry';

export const STORAGE_KEY = CANONICAL_STORAGE_KEY;

export interface SoundSettings {
  tradeSoundEnabled: boolean;
  whaleSoundEnabled: boolean;
  /** Doom 10-17: fills of Penelope's (bot) orders ring only while this is on; your own fills are unaffected. */
  botFillSoundEnabled: boolean;
  firstSaleSoundEnabled: boolean;
  defenseSoundEnabled: boolean;
  sweepSoundEnabled: boolean;
  exposureBreachSoundEnabled: boolean;
  adverseFlowSoundEnabled: boolean;
  queueFrontSoundEnabled: boolean;
  bestPriceSoundEnabled: boolean;
  /** Cross-venue alert: Polymarket mid diverges >= 2c from Kalshi on a market
   *  you hold an order/position in. */
  divergenceSoundEnabled: boolean;
  /** Also ring the divergence chime for games ALREADY IN PROGRESS. Off by
   *  default (Josh's ticket via janet + Dan/client feedback, Jul-3): in-play
   *  books legitimately diverge, so for non-live traders the chime is noise
   *  once a game starts. The visual badge/flash is unaffected either way. */
  divergenceLiveGameSoundEnabled: boolean;
  orderPlacedSoundEnabled: boolean;
  orderRejectionSoundEnabled: boolean;
  bootSoundEnabled: boolean;
  volume: number; // 0-1
  /** Minimum contracts in a fill to trigger sound (0 = no minimum) */
  minFillContracts: number;
}

export const SOUND_DEFAULTS: SoundSettings = {
  tradeSoundEnabled: true,
  whaleSoundEnabled: true,
  botFillSoundEnabled: true,
  firstSaleSoundEnabled: true,
  defenseSoundEnabled: true,
  sweepSoundEnabled: true,
  exposureBreachSoundEnabled: true,
  adverseFlowSoundEnabled: true,
  queueFrontSoundEnabled: true,
  bestPriceSoundEnabled: true,
  divergenceSoundEnabled: true,
  divergenceLiveGameSoundEnabled: false,
  orderPlacedSoundEnabled: true,
  orderRejectionSoundEnabled: true,
  bootSoundEnabled: true,
  volume: 0.5,
  minFillContracts: 0,
};

/**
 * Channels whose legacy key maps 1:1 — everything except the queue trio and,
 * since #233, except channels with NO legacy key at all (the velocity channels
 * are new, so there is no flat boolean to read from or mirror to). Carrying the
 * key alongside the id keeps the null narrowed for every consumer below.
 */
const ONE_TO_ONE_CHANNELS: ReadonlyArray<{
  id: AlertChannelId;
  legacyKey: LegacySoundToggleKey;
}> = ALERT_CHANNEL_IDS.flatMap((id) => {
  const legacyKey = ALERT_REGISTRY[id].legacyKey;
  return legacyKey === null || legacyKey === 'queueFrontSoundEnabled'
    ? []
    : [{ id, legacyKey }];
});

/** The legacy view of the canonical state. */
export function toLegacySoundSettings(prefs: AlertPreferences): SoundSettings {
  const legacy = { ...SOUND_DEFAULTS };
  for (const { id, legacyKey } of ONE_TO_ONE_CHANNELS) {
    legacy[legacyKey] = prefs.channels[id].soundEnabled;
  }
  legacy.queueFrontSoundEnabled =
    prefs.channels.queueWarning.soundEnabled ||
    prefs.channels.queueCritical.soundEnabled ||
    prefs.channels.fellBehind.soundEnabled;
  legacy.volume = prefs.masterVolume;
  legacy.minFillContracts = prefs.minFillContracts;
  return legacy;
}

export function readSoundSettings(): SoundSettings {
  return toLegacySoundSettings(readAlertPreferences());
}

/** The queue master's three canonical channels, in registry order. */
const QUEUE_TRIO: AlertChannelId[] = ['queueWarning', 'queueCritical', 'fellBehind'];

/**
 * Patch-based legacy write — the path legacy callers should use.
 *
 * Reads current canonical truth and applies ONLY the keys the caller explicitly
 * passed. A caller holding a stale snapshot therefore cannot revert a
 * legacy-representable field a sibling tab (or a canonical writer in this tab)
 * has since moved: an untouched key simply isn't in the patch.
 *
 * Returns the PERSISTED legacy view — a fresh read after the write — so callers
 * adopt what actually landed (volume clamped, minFillContracts floored, the
 * queue master re-derived as the OR of the trio) rather than their own
 * pre-write guess.
 */
export function patchSoundSettings(patch: Partial<SoundSettings>): SoundSettings {
  const current = readAlertPreferences();

  for (const { id, legacyKey } of ONE_TO_ONE_CHANNELS) {
    const next = patch[legacyKey];
    if (next !== undefined) {
      current.channels[id] = { ...current.channels[id], soundEnabled: next };
    }
  }

  // The trio stays diff-guarded even under explicit-key semantics: the 3->1
  // mapping is lossy, so re-asserting the SAME master value must not flatten a
  // per-channel split. A real user flip always differs from the OR'd view.
  if (
    patch.queueFrontSoundEnabled !== undefined &&
    patch.queueFrontSoundEnabled !== toLegacySoundSettings(current).queueFrontSoundEnabled
  ) {
    for (const id of QUEUE_TRIO) {
      current.channels[id] = {
        ...current.channels[id],
        soundEnabled: patch.queueFrontSoundEnabled,
      };
    }
  }

  if (patch.volume !== undefined) current.masterVolume = patch.volume;
  if (patch.minFillContracts !== undefined) current.minFillContracts = patch.minFillContracts;

  writeAlertPreferences(current);
  return readSoundSettings();
}

/**
 * Whole-object legacy write. Retained for a DELIBERATE full replacement; a
 * caller changing specific fields wants `patchSoundSettings` instead, which is
 * immune to the stale-snapshot revert documented at the top of this file.
 */
export function writeSoundSettings(settings: SoundSettings): void {
  const current = readAlertPreferences();
  const view = toLegacySoundSettings(current);

  for (const { id, legacyKey: key } of ONE_TO_ONE_CHANNELS) {
    if (settings[key] !== view[key]) {
      current.channels[id] = { ...current.channels[id], soundEnabled: settings[key] };
    }
  }
  if (settings.queueFrontSoundEnabled !== view.queueFrontSoundEnabled) {
    for (const id of QUEUE_TRIO) {
      current.channels[id] = {
        ...current.channels[id],
        soundEnabled: settings.queueFrontSoundEnabled,
      };
    }
  }
  current.masterVolume = settings.volume;
  current.minFillContracts = settings.minFillContracts;

  writeAlertPreferences(current);
}
